import { useMemo } from "react";
import { useClinics } from "@api/hooks";
import { clinicLabel } from "@utils/utils";

/**
 * Shared clinic-filter option builder used by the cash-flow pages.
 *
 * Loads clinic profiles and derives the area-manager and clinic dropdown
 * options, optionally scoped to a selected area manager.
 *
 * @param {string} areaManagerFilter — selected area manager (empty = all)
 * @param {{enabled?: boolean, includeInactive?: boolean}} options
 *   - `enabled: false` skips the clinics fetch entirely (e.g. Daily Bank Balance
 *     in entity mode, which has no clinic filters to populate).
 *   - `includeInactive: true` includes sold/closed clinics — needed by
 *     features that show historical data (e.g. Daily Bank Balance), since
 *     the default clinics list is active-only and would otherwise silently
 *     hide rows for a clinic that has since been sold/closed.
 * @returns {{
 *   clinicsQuery: object,
 *   allProfiles: object[],
 *   areaManagerOptions: {value: string, label: string}[],
 *   clinicOptions: object[],
 * }}
 */
export default function useClinicFilterOptions(
  areaManagerFilter = "",
  { enabled = true, includeInactive = false } = {},
) {
  const clinicsQuery = useClinics(includeInactive ? { include_inactive: true } : {}, { enabled });
  const allProfiles = useMemo(() => clinicsQuery.data?.results ?? [], [clinicsQuery.data?.results]);

  const areaManagerOptions = useMemo(() => {
    const seen = new Set();
    return allProfiles
      .map((c) => String(c.area_manager || "").trim())
      .filter((m) => m && !seen.has(m.toLowerCase()) && seen.add(m.toLowerCase()))
      .sort((a, b) => a.localeCompare(b))
      .map((m) => ({ value: m, label: m }));
  }, [allProfiles]);

  const clinicOptions = useMemo(() => {
    const profiles = areaManagerFilter
      ? allProfiles.filter((c) => String(c.area_manager || "").trim() === areaManagerFilter)
      : allProfiles;
    return profiles
      .filter((c) => c.clinic)
      .map((c) => {
        const code = c.clinic;
        const name = (c.name || c.clinic_name || "").trim() || code;
        const manager = String(c.area_manager || "").trim();
        return {
          value: code,
          label: clinicLabel(c),
          name,
          code,
          // Numeric PK, for endpoints that filter by `clinic_id` instead of the
          // code (e.g. daily-bank-balance / bank-accounts) — an indexed integer
          // FK compare rather than a string compare on a joined clinics row.
          id: c.id,
          status: c.status,
          subtitle: manager ? `${code} · ${manager}` : code,
        };
      })
      .sort((a, b) => {
        if (a.value === "SFC") return -1;
        if (b.value === "SFC") return 1;
        return a.name.localeCompare(b.name);
      });
  }, [allProfiles, areaManagerFilter]);

  return { clinicsQuery, allProfiles, areaManagerOptions, clinicOptions };
}
